import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRightLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Badge, Button, Card } from "@/components/ui";
import { api, ApiError, type ChannelMigration } from "@/lib/api";

const STEP: Record<ChannelMigration["items"][number]["step"], { label: string; tone: "neutral" | "info" | "success" | "warning" | "danger" }> = {
  waiting: { label: "wartet", tone: "neutral" },
  cancel_requested: { label: "Stornierung angefragt", tone: "info" },
  requested: { label: "bei Logiful angefordert", tone: "info" },
  done: { label: "übernommen", tone: "success" },
  kept: { label: "bleibt bei FFN Connect", tone: "warning" },
  failed: { label: "prüfen", tone: "danger" },
};
const day = (s: string) => new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
const errText = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehlgeschlagen") : "Fehlgeschlagen");

/**
 * Logiful intern: offene Bestellungen von FFN Connect zu Logiful umziehen (Stornierungsanfrage → Lager prüfen →
 * Standort wechseln → anfordern). Während des Umzugs ist die Übergabe ans Lager für den Kunden angehalten, damit die
 * Bestandsvergabe danach streng nach Shopify-Bestelldatum entscheidet.
 */
export function ChannelMigrationCard({ tenantId, channelId }: { tenantId: string; channelId: string }) {
  const qc = useQueryClient();
  const key = ["channel-migration", tenantId, channelId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.admin.channelMigration(tenantId, channelId),
    refetchInterval: (query) => (["previewing", "running"].includes(query.state.data?.migration?.status ?? "") ? 5000 : false),
  });
  const m = q.data?.migration ?? null;
  const [location, setLocation] = useState("");
  const [selected, setSelected] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (m?.status === "preview" && !location && m.locations[0]) setLocation(m.locations.find((l) => /ffn|jtl/i.test(l.name))?.id ?? m.locations[0].id);
  }, [m, location]);
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const preview = useMutation({ mutationFn: () => api.admin.channelMigrationPreview(tenantId, channelId), onSuccess: () => { setSelected(null); refresh(); }, onError: (e) => toast.error(errText(e)) });
  const start = useMutation({
    mutationFn: (ids: string[] | undefined) => api.admin.channelMigrationStart(tenantId, channelId, location, ids),
    onSuccess: () => { toast.success("Umzug gestartet", { description: "Übergabe ans Lager für diesen Kunden ist bis zum Abschluss angehalten." }); refresh(); },
    onError: (e) => toast.error(errText(e)),
  });
  const stop = useMutation({ mutationFn: () => api.admin.channelMigrationStop(tenantId, channelId), onSuccess: () => { toast.success("Umzug abgebrochen – Übergabe wieder frei"); refresh(); }, onError: (e) => toast.error(errText(e)) });

  const items = useMemo(() => (m ? m.items.filter((i) => m.status !== "preview" || i.locationId === location) : []), [m, location]);
  const movable = items.filter((i) => i.step === "waiting");
  const chosen = selected ?? new Set(movable.map((i) => i.fulfillmentOrderId));
  const count = (s: string) => items.filter((i) => i.step === s).length;

  return (
    <Card className="p-5">
      <div className="flex items-start gap-3">
        <ArrowRightLeft className="size-5 text-fg-muted mt-0.5" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[15px] font-semibold">Umzug von FFN Connect <span className="ml-1 text-[11px] font-medium text-fg-subtle">Logiful intern</span></h2>
          <p className="mt-0.5 text-[12.5px] text-fg-muted">
            Offene Bestellungen, die in Shopify noch bei FFN Connect liegen, zu Logiful holen: Stornierung bei FFN Connect anfragen (die Bestellung selbst bleibt unverändert), im Lager prüfen, dass der Auftrag von FFN Connect storniert ist, dann auf den Logiful-Standort verschieben und anfordern. Solange angehalten: keine Übergabe ans Lager für diesen Kunden – danach Bestandsvergabe streng nach Shopify-Bestelldatum.
          </p>
        </div>
        {(!m || ["preview", "done", "stopped", "failed"].includes(m.status)) && (
          <Button size="sm" variant="secondary" loading={preview.isPending} onClick={() => preview.mutate()}>{m ? "Vorschau neu laden" : "Vorschau laden"}</Button>
        )}
      </div>

      {m?.status === "previewing" && <p className="mt-4 text-[13px] text-fg-muted">Vorschau wird berechnet – offene Bestellungen in Shopify und Lageraufträge in FFN werden abgeglichen …</p>}
      {m?.status === "failed" && <p className="mt-4 text-[13px] text-danger">Vorschau fehlgeschlagen: {m.error}</p>}

      {m && ["preview", "running", "done", "stopped"].includes(m.status) && (
        <div className="mt-4 space-y-3">
          {m.status === "preview" && (
            m.locations.length ? (
              <div className="flex flex-wrap items-center gap-2 text-[13px]">
                <span className="text-fg-muted">Bisherige Fulfillment-App (Standort):</span>
                <select value={location} onChange={(e) => { setLocation(e.target.value); setSelected(null); }} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
                  {m.locations.map((l) => <option key={l.id} value={l.id}>{l.name} ({l.count})</option>)}
                </select>
                <span className="text-fg-subtle">
                  {m.outboundPrefix ? <>Lageraufträge in FFN mit Präfix <span className="font-mono">{m.outboundPrefix}-</span> zugeordnet</> : "Lageraufträge in FFN nach Bestellnummer zugeordnet"}
                </span>
              </div>
            ) : (
              <p className="text-[13px] text-fg-muted">Keine offenen Bestellungen an anderen Standorten gefunden.</p>
            )
          )}
          {m.status !== "preview" && (
            <p className="text-[13px]">
              {m.fromLocationName ?? "FFN Connect"} · {count("done")} übernommen · {count("kept")} bleiben · {count("failed")} prüfen · {items.length - count("done") - count("kept") - count("failed")} in Arbeit
              {m.status === "running" && m.pauseUntil && <span className="text-warning"> · Übergabe angehalten bis spätestens {day(m.pauseUntil)}</span>}
              {m.status === "done" && m.finishedAt && <span className="text-fg-muted"> · abgeschlossen {day(m.finishedAt)}</span>}
              {m.status === "stopped" && <span className="text-fg-muted"> · abgebrochen</span>}
            </p>
          )}
          {items.length > 0 && (
            <div className="max-h-[360px] overflow-y-auto rounded-xl border border-border">
              <table className="w-full text-[12.5px]">
                <thead className="sticky top-0 bg-surface-2 text-fg-subtle">
                  <tr>
                    {m.status === "preview" && <th className="w-8 px-2 py-1.5"><input type="checkbox" aria-label="alle" checked={chosen.size === movable.length && movable.length > 0} onChange={(e) => setSelected(e.target.checked ? null : new Set())} /></th>}
                    <th className="px-2 py-1.5 text-left font-medium">Bestellung</th>
                    <th className="px-2 py-1.5 text-left font-medium">Bestelldatum</th>
                    <th className="px-2 py-1.5 text-left font-medium">Lagerauftrag (FFN)</th>
                    <th className="px-2 py-1.5 text-left font-medium">Stand</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((i) => (
                    <tr key={i.fulfillmentOrderId}>
                      {m.status === "preview" && (
                        <td className="px-2 py-1.5">
                          {i.step === "waiting" && <input type="checkbox" aria-label={i.orderName} checked={chosen.has(i.fulfillmentOrderId)} onChange={(e) => { const n = new Set(chosen); if (e.target.checked) n.add(i.fulfillmentOrderId); else n.delete(i.fulfillmentOrderId); setSelected(n); }} />}
                        </td>
                      )}
                      <td className="px-2 py-1.5 font-mono">{i.orderName}</td>
                      <td className="px-2 py-1.5 tabular-nums">{day(i.orderCreatedAt)}</td>
                      <td className="px-2 py-1.5">{i.outboundRef ? `${i.outboundRef} · ${i.outboundStatus ?? ""}` : "—"}</td>
                      <td className="px-2 py-1.5">
                        <Badge tone={STEP[i.step].tone}>{STEP[i.step].label}</Badge>
                        {i.message && <span className="block text-[11.5px] text-fg-muted">{i.message}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {m.status === "preview" && movable.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="primary"
                disabled={!chosen.size}
                loading={start.isPending}
                onClick={() => {
                  if (!confirm(`${chosen.size} Bestellungen von FFN Connect zu Logiful umziehen?\n\n• Die Übergabe ans Lager ist für diesen Kunden bis zum Abschluss angehalten (höchstens 3 Stunden).\n• FFN Connect bekommt je Bestellung eine Stornierungsanfrage – die Bestellung selbst bleibt unverändert.\n• Verschoben wird erst, wenn der Lagerauftrag von FFN Connect im Lager storniert ist.`)) return;
                  start.mutate(chosen.size === movable.length ? undefined : [...chosen]);
                }}
              >
                Umzug starten ({chosen.size})
              </Button>
              <span className="text-[12px] text-fg-subtle">Tipp: zuerst eine einzelne Bestellung umziehen und prüfen.</span>
            </div>
          )}
          {m.status === "running" && (
            <Button variant="ghost" loading={stop.isPending} onClick={() => { if (confirm("Umzug abbrechen? Bereits verschobene Bestellungen bleiben bei Logiful, die Übergabe ans Lager läuft sofort wieder.")) stop.mutate(); }}>
              Abbrechen
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
